import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import CatalogChart, { QueryResult } from '../analytics/CatalogChart';
import MultiSeriesChart, { CorrelationPanel, ComparisonResult } from '../analytics/MultiSeriesChart';

export interface GraphSeries {
  source: string;
  measure: string;
  filters?: { key: string; value: unknown }[];
  label?: string;
  axis?: 'left' | 'right';
}

export interface GraphSpec {
  source?: string;
  measure?: string;
  measures?: string[];
  groupBy?: string | null;
  filters?: { key: string; value: unknown }[];
  timeRange?: string | null;
  bucket?: string | null;
  limit?: number | null;
  // Comparison mode.
  series?: GraphSeries[];
  scaleMode?: 'dual' | 'normalized';
}

export interface CustomGraph {
  _id: string;
  title: string;
  description?: string;
  chartType: string;
  spec: GraphSpec;
}

const Spinner: React.FC = () => (
  <div className="d-flex justify-content-center py-4">
    <div className="spinner-border" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

const SavedGraphCard: React.FC<{ graph: CustomGraph; onDelete: (id: string) => void }> = ({ graph, onDelete }) => {
  const isComparison = Array.isArray(graph.spec?.series) && graph.spec.series.length > 0;
  const [result, setResult] = useState<QueryResult | null>(null);
  const [multiResult, setMultiResult] = useState<ComparisonResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const endpoint = isComparison ? 'query-multi' : 'query';
    const payload = isComparison
      ? { series: graph.spec.series, timeRange: graph.spec.timeRange, bucket: graph.spec.bucket }
      : graph.spec;
    axios
      .post(`${API_BASE_URL}/analytics/${endpoint}`, payload, { withCredentials: true })
      .then((res) => {
        if (!cancelled) {
          if (isComparison) setMultiResult(res.data);
          else setResult(res.data);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.message || 'Failed to load graph');
      });
    return () => {
      cancelled = true;
    };
  }, [graph.spec, isComparison]);

  return (
    <section className="store-dash-chart">
      <div className="store-dash-chart-head">
        <h2>{graph.title}</h2>
        <div className="d-flex gap-2 align-items-center">
          <Link to={`/custom/${graph._id}`}>Edit</Link>
          <button
            type="button"
            className="btn btn-link btn-sm text-danger p-0"
            onClick={() => onDelete(graph._id)}
          >
            Delete
          </button>
        </div>
      </div>
      {graph.description && <p className="store-dash-hint">{graph.description}</p>}
      {error ? (
        <div className="alert alert-danger mb-0">{error}</div>
      ) : isComparison ? (
        multiResult ? (
          <>
            <MultiSeriesChart
              result={multiResult}
              chartType={graph.chartType}
              scaleMode={graph.spec.scaleMode || 'dual'}
            />
            <CorrelationPanel result={multiResult} />
          </>
        ) : (
          <Spinner />
        )
      ) : result ? (
        <CatalogChart result={result} chartType={graph.chartType} />
      ) : (
        <Spinner />
      )}
    </section>
  );
};

const StoreDashboardCustomView: React.FC = () => {
  const [graphs, setGraphs] = useState<CustomGraph[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/analytics/graphs`, { withCredentials: true });
      setGraphs(res.data?.graphs || []);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load custom graphs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this graph?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/analytics/graphs/${id}`, { withCredentials: true });
      setGraphs((current) => current.filter((g) => g._id !== id));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete graph');
    }
  };

  return (
    <>
      <div className="alert alert-warning fw-semibold" role="alert">
        ⚠️ These controls are still in development — the data and visualizations may not be accurate.
      </div>

      <div className="store-dash-chart-head">
        <h2>Custom graphs</h2>
        <Link to="/custom/new" className="btn btn-primary btn-sm">
          Create Graph
        </Link>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <Spinner />
      ) : graphs.length === 0 ? (
        <div
          className="d-flex flex-column align-items-center justify-content-center text-center"
          style={{
            minHeight: '320px',
            background: '#f1f3f5',
            border: '2px dashed #ced4da',
            borderRadius: '0.75rem',
            color: '#868e96',
          }}
        >
          <p className="mb-3" style={{ fontSize: '1.05rem' }}>No custom graphs yet.</p>
          <Link to="/custom/new" className="btn btn-primary">
            Create your first graph
          </Link>
        </div>
      ) : (
        <div className="store-dash-charts">
          {graphs.map((graph) => (
            <SavedGraphCard key={graph._id} graph={graph} onDelete={handleDelete} />
          ))}
        </div>
      )}
    </>
  );
};

export default StoreDashboardCustomView;
